import { useEffect, useMemo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Col, Container, HeadingLevel, Layout, Row } from "@viagogo/core/ui";
import { useFeatureTernary } from "@viagogo/feature";
import { GridType } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PAGE_IDS } from "src/shared/constants/PageIds";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, useLocationContext } from "src/shared/context/location-context";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation.ts";
import { deobfuscateToken } from "src/shared/utils/tokenUtils";
import { TopResultDemotionWrapper } from "src/website-apps/responsive/stubhub-find/components/TopResults/styled-components";
import { TopResultPills } from "src/website-apps/responsive/stubhub-find/components/TopResults/TopResultPills";
import { useFindPageModelData } from "src/website-apps/responsive/stubhub-find/hooks/useFindPageModelData";
import { AppInstallBanner, AppInstallBannerPlacements } from "src/website-apps/shared/components/app-install-banner";
import { withDefaultContexts } from "src/website-apps/shared/components/explore/withDefaultContexts";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { ALL_DATES_OPTION } from "src/website-apps/shared/constants/date-options.ts";
import { useDateCookieOptions, useDatesContext } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { SearchTermProvider } from "src/website-apps/shared/context/SearchTermContext";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useWebNativeHeader } from "src/website-apps/shared/hooks/useWebNativeHeader";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import { isPerformerPage } from "src/website-apps/shared/utils/isPerformerPage";
import { isSearchPage } from "src/website-apps/shared/utils/isSearchPage";
import { resolveInitialFavoritesState } from "../utils/resolveInitialFavoritesState";
import { ExploreModeSearchApp } from "./ExploreModeSearchApp";
import { FindEmailSignUp } from "./FindEmailSignUp";
import { FindEventListRecentlyViewedGrid } from "./FindEventListRecentlyViewedGrid";
import { FindLayoutFooter } from "./FindLayoutFooter";
import { FindLayoutHeader } from "./FindLayoutHeader";
import { RecommendedForYouInSearch } from "./RecommendedForYouInSearch";
import { hasSearchResults, NoSearchResultsTips } from "./search-result-empty-state";
import { TrendingEventsInSearch } from "./TrendingEvents";

const bodyWrapper = css({
  width: "full",
  height: "full",
  paddingBlockStart: "primitives.200",
});

const backgroundColor = css({
  backgroundColor: "bg.surface",
});

const UnwrappedApp = () => {
  const model = useFindPageModelData();
  const appContext = useAppContext();
  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const [userLocation] = useLocationContext(cookieOptions);
  const dateCookieOptions = useDateCookieOptions();
  const [userDates] = useDatesContext(false, dateCookieOptions);
  const isPhone = !useMediaQuery(Breakpoint.Tablet);
  const [mobileMenuOpen, setMobileMenuOpen] = useScrollToggle();
  const { search } = useWindowLocation();

  const filters = getFilterQueryParams(search);

  const {
    eventGrids,
    gridFilterTypes,
    privacyPolicyMessage,
    recommendedCategories = {
      categoryId: 0,
      categoryItems: { items: [], totalCount: 0, pageSize: 30, remaining: 0, pageIndex: 0 },
    },
    sellSearch,
    topSearchResults,
    sortedBy,
  } = model;

  const searchKeyword = JSON.parse(`"${model.searchKeyword}"`);
  const searchGuid = JSON.parse(`"${model.searchGuid}"`);
  const obfuscatedAuth = JSON.parse(`"${model.auth}"`);
  const auth = deobfuscateToken(obfuscatedAuth, searchKeyword);

  // Remove allowExplore query parameter from URL to prevent it from persisting across requests
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("allowExplore")) {
      url.searchParams.delete("allowExplore");
      window.history.replaceState({}, "", url);
    }
  }, []);

  const defaultGridFilterType = gridFilterTypes?.length === 1 ? gridFilterTypes[0] : filters.gridFilterType;

  const hasNoResults = useMemo(() => !hasSearchResults(model), [model]);

  // Check if we should show filters even with no results
  const searchResultLocationFilterTernary = useFeatureTernary(FeatureSetEnum.TMSearchResultFunctionalLocationFilter);
  const shouldShowFiltersWhenEmpty = useMemo(
    () =>
      !!searchResultLocationFilterTernary({
        control: () => false,
        variant: () => true,
      }),
    [searchResultLocationFilterTernary],
  );

  const initialCountryGridDataRecorder = useFeatureTernary(
    FeatureSetEnum.TMELCountryGrid,
    () => isPerformerPage(appContext) || isSearchPage(appContext),
  );
  const initialCountryGridData = useMemo(
    () =>
      initialCountryGridDataRecorder({
        control: () => null,
        variant: (countryGridData) => countryGridData,
        record: false,
      }),
    [initialCountryGridDataRecorder],
  );

  const resultsGrid = useMemo(
    () => (
      <Row between={[true]}>
        <Col span={[12]}>
          <FiltersAndGridsManager
            filterTypeOptions={gridFilterTypes ?? []}
            searchKeyWord={searchKeyword}
            hideNoLocalEventsTitle={false}
          />
        </Col>
      </Row>
    ),
    [gridFilterTypes, searchKeyword],
  );

  const { showWebHeaderForNativeApp } = useWebNativeHeader(appContext.pageId);

  const reducerOptions = useMemo(
    () => ({
      dates: userDates ?? ALL_DATES_OPTION,
      genreId: filters.genreId,
      gridFilterType: defaultGridFilterType,
      location: userLocation,
      method: "GetSearchEvents",
      primaryGrid: eventGrids?.[GridType.PrimaryRadius],
      primaryFromRadius: null,
      primaryToRadius: null,
      restGrid: { disabled: true },
      searchKeyWord: searchKeyword,
      sellSearch,
      sortBy: sortedBy,
      countryGridData: initialCountryGridData,
      searchGuid,
      auth,
    }),
    [
      auth,
      defaultGridFilterType,
      eventGrids,
      filters.genreId,
      initialCountryGridData,
      searchGuid,
      searchKeyword,
      sellSearch,
      sortedBy,
      userDates,
      userLocation,
    ],
  );
  const gridsContext = useGridsReducer(reducerOptions);

  // Render explore mode if active
  if (model.isExploreMode) {
    return <ExploreModeSearchApp />;
  }

  return (
    <div className={backgroundColor}>
      <SearchTermProvider searchTerm={searchKeyword}>
        <MegaSearchProvider>
          <div
            className={pageWrapperRecipe({
              mobileMenu: mobileMenuOpen ? "open" : "closed",
              header: showWebHeaderForNativeApp && appContext.pageId === PAGE_IDS.SEARCH ? "nativeApp" : "site",
            })}
          >
            <EventsGridContext.Provider value={gridsContext}>
              <HeadingLevel>
                <Layout
                  header={
                    <FindLayoutHeader
                      mobileMenuOpen={mobileMenuOpen}
                      model={model}
                      setMobileMenuOpen={setMobileMenuOpen}
                    />
                  }
                  body={
                    <FavoritesProvider data={resolveInitialFavoritesState(model)}>
                      <Container>
                        <TopResultDemotionWrapper>
                          {/* @ts-expect-error -- Backend model is using mixed serializers */}
                          <TopResultPills topSearchResults={topSearchResults?.searchResults} />
                        </TopResultDemotionWrapper>
                        <div className={bodyWrapper}>
                          {hasNoResults && shouldShowFiltersWhenEmpty ? (
                            <>
                              {resultsGrid}
                              <NoSearchResultsTips searchKeyword={searchKeyword ?? ""} reducedTopMargin />
                            </>
                          ) : hasNoResults ? (
                            <NoSearchResultsTips searchKeyword={searchKeyword ?? ""} reducedTopMargin />
                          ) : (
                            resultsGrid
                          )}
                          <TrendingEventsInSearch hasNoResults={hasNoResults} />
                          <RecommendedForYouInSearch
                            hasNoResults={hasNoResults}
                            recommendations={recommendedCategories}
                          />
                          <FindEventListRecentlyViewedGrid sellSearch={sellSearch} isPhone={isPhone} />
                          <FindEmailSignUp privacyPolicyMessage={privacyPolicyMessage} />
                          <AppInstallBanner placement={AppInstallBannerPlacements.SEARCH} userId={appContext.userId} />
                        </div>
                      </Container>
                    </FavoritesProvider>
                  }
                  footer={<FindLayoutFooter model={model} />}
                />
              </HeadingLevel>
            </EventsGridContext.Provider>
          </div>
        </MegaSearchProvider>
      </SearchTermProvider>
    </div>
  );
};

export const App = withDefaultContexts(UnwrappedApp);
